import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Timer, Play, Square, AlertTriangle, Pencil, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import api from '@/lib/api';

/* Zeiterfassung fürs Team (Betreiber 12.10.2026): Stempeluhr auf der Heute-Seite. Jeder stempelt für sich; der Inhaber
   sieht in „Stunden" alle, trägt nach und berichtigt. Der Server rechnet — die Uhr hier zählt nur zwischen zwei Abfragen. */

const zwei = (n) => String(n).padStart(2, '0');
const dauer = (min) => `${Math.floor(min / 60)}:${zwei(min % 60)} h`;
const uhr = (iso) => iso ? new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : '';
const tagText = (tag) => new Date(`${tag}T12:00`).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
const hhmm = (iso) => { const d = new Date(iso); return `${zwei(d.getHours())}:${zwei(d.getMinutes())}`; };
const heuteIso = () => { const d = new Date(); return `${d.getFullYear()}-${zwei(d.getMonth() + 1)}-${zwei(d.getDate())}`; };
const stdText = (min) => `${(min / 60).toFixed(1).replace('.', ',')} h`;
const fehler = (e, ersatz) => toast.error(e?.response?.data?.detail || ersatz);
const feld = 'h-9 rounded-lg border border-input bg-background px-2 text-sm';

export default function Zeiterfassung() {
  const [s, setS] = useState(null);
  const [sek, setSek] = useState(0);
  const [stoppen, setStoppen] = useState(false);
  const [pause, setPause] = useState('0');
  const [notiz, setNotiz] = useState('');
  const [ende, setEnde] = useState('');
  const [stunden, setStunden] = useState(false);
  const [busy, setBusy] = useState(false);

  const laden = useCallback(() => api.get('/zeiterfassung/stand').then(r => setS(r.data)).catch(() => {}), []);
  useEffect(() => { laden(); const t = setInterval(laden, 60000); return () => clearInterval(t); }, [laden]);
  useEffect(() => {
    if (!s?.laufend) { setSek(0); return undefined; }
    const beginn = new Date(s.laufend.start).getTime();
    const tick = () => setSek(Math.max(0, Math.floor((Date.now() - beginn) / 1000)));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [s]);

  if (!s) return null;
  const laeuft = !!s.laufend;
  const start = async () => {
    setBusy(true);
    try { await api.post('/zeiterfassung/start'); toast.success('Zeit läuft'); await laden(); } catch (e) { fehler(e, 'Nicht gestartet'); }
    setBusy(false);
  };
  const stopp = async () => {
    setBusy(true);
    try {
      const koerper = { pause_min: Number(pause) || 0, notiz };
      if (ende) koerper.ende = new Date(`${s.laufend.tag}T${ende}`).toISOString();
      const r = (await api.post('/zeiterfassung/stopp', koerper)).data;
      toast.success(`Gestoppt: ${dauer(r.minuten)}`);
      setStoppen(false); setPause('0'); setNotiz(''); setEnde(''); await laden();
    } catch (e) { fehler(e, 'Nicht gestoppt'); }
    setBusy(false);
  };

  return (
    <Card data-testid="zeiterfassung">
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <div className="min-w-0">
            <h2 className="text-sm font-bold flex items-center gap-1.5"><Timer className="w-4 h-4 text-primary" /> Zeiterfassung</h2>
            <p className="text-xs text-muted-foreground">{laeuft ? `läuft seit ${uhr(s.laufend.start)} Uhr` : 'Stempel ein, wenn du anfängst.'}</p>
          </div>
          <div className={`text-3xl font-extrabold tabular-nums tracking-tight transition-colors ${laeuft ? 'text-primary' : 'text-muted-foreground/50'}`} aria-live="off" data-testid="zeit-uhr">
            {zwei(Math.floor(sek / 3600))}:{zwei(Math.floor(sek / 60) % 60)}:{zwei(sek % 60)}
          </div>
          <div className="flex gap-4 text-xs text-muted-foreground">
            <span>Heute <b className="text-foreground tabular-nums">{dauer(s.heute_min)}</b></span>
            <span>Diese Woche <b className="text-foreground tabular-nums">{dauer(s.woche_min)}</b></span>
          </div>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setStunden(true)} data-testid="zeit-stunden">Stunden</Button>
            {laeuft
              ? <Button size="sm" variant="outline" disabled={busy} onClick={() => setStoppen(v => !v)} data-testid="zeit-stopp"><Square className="w-4 h-4" /> Stoppen</Button>
              : <Button size="sm" disabled={busy} onClick={start} data-testid="zeit-start"><Play className="w-4 h-4" /> Starten</Button>}
          </div>
        </div>
        {laeuft && s.zu_lange && (
          <p className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400"><AlertTriangle className="w-3.5 h-3.5 flex-none" />
            Die Zeit läuft seit über zwölf Stunden. Falls du vergessen hast auszustempeln: beim Stoppen das wirkliche Ende angeben.</p>
        )}
        {laeuft && stoppen && (
          <div className="flex flex-wrap items-end gap-3 rounded-xl bg-muted/60 p-3">
            <label className="text-xs">Pause in Minuten<br /><input id="zeit-pause" type="number" min="0" max="600" value={pause} onChange={e => setPause(e.target.value)} className={`${feld} w-24`} /></label>
            <label className="text-xs">Wirkliches Ende (nur wenn vergessen)<br /><input id="zeit-ende" type="time" value={ende} onChange={e => setEnde(e.target.value)} className={`${feld} w-32`} /></label>
            <label className="text-xs flex-1 min-w-[160px]">Notiz<br /><input id="zeit-notiz" value={notiz} maxLength={300} onChange={e => setNotiz(e.target.value)} className={`${feld} w-full`} placeholder="optional" /></label>
            <Button size="sm" disabled={busy} onClick={stopp} data-testid="zeit-stopp-bestaetigen">Jetzt stoppen</Button>
          </div>
        )}
      </CardContent>
      {stunden && <StundenDialog onClose={() => { setStunden(false); laden(); }} />}
    </Card>
  );
}

function StundenDialog({ onClose }) {
  const [monat, setMonat] = useState(heuteIso().slice(0, 7));
  const [wer, setWer] = useState(null);
  const [d, setD] = useState(null);
  const [form, setForm] = useState(null);          // {id?, tag, von, bis, pause_min, notiz, grund}
  const [vertrag, setVertrag] = useState(null);
  const laden = useCallback(() => api.get('/zeiterfassung/eintraege', { params: { monat, ...(wer ? { person: wer } : {}) } })
    .then(r => { setD(r.data); setVertrag(r.data.vertrag); }).catch(e => fehler(e, 'Nicht geladen')), [monat, wer]);
  useEffect(() => { laden(); }, [laden]);

  const herunterladen = async (format) => {
    try {
      const r = await api.get('/zeiterfassung/export', { params: { monat, format, ...(wer ? { person: wer } : {}) }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `arbeitszeiten-${monat}.${format}`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { fehler(e, 'Export nicht möglich'); }
  };
  const freigeben = async (id) => {
    try { await api.post(`/zeiterfassung/eintraege/${id}/freigeben`); toast.success('Freigegeben'); laden(); } catch (e) { fehler(e, 'Nicht freigegeben'); }
  };
  const vertragSpeichern = async () => {
    try {
      await api.put(`/zeiterfassung/personen/${encodeURIComponent(d.person.key)}/vertrag`, {
        art: vertrag.art, stunden_woche: vertrag.stunden_woche === '' ? null : vertrag.stunden_woche, stundenlohn: vertrag.stundenlohn === '' ? null : vertrag.stundenlohn });
      toast.success('Gespeichert'); laden();
    } catch (e) { fehler(e, 'Nicht gespeichert'); }
  };

  const speichern = async () => {
    const koerper = { tag: form.tag, von: form.von, bis: form.bis, pause_min: Number(form.pause_min) || 0, notiz: form.notiz || '', grund: form.grund || null };
    try {
      if (form.id) await api.put(`/zeiterfassung/eintraege/${form.id}`, koerper);
      else await api.post('/zeiterfassung/eintraege', { ...koerper, ...(wer ? { person: wer } : {}) });
      toast.success(form.id ? 'Geändert' : 'Nachgetragen'); setForm(null); laden();
    } catch (e) { fehler(e, 'Nicht gespeichert'); }
  };
  const loeschen = async (e) => {
    try { await api.delete(`/zeiterfassung/eintraege/${e.id}`); toast.success('Gelöscht'); laden(); } catch (x) { fehler(x, 'Nicht gelöscht'); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Stunden {d?.person?.name ? `von ${d.person.name}` : ''}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <input id="zeit-monat" type="month" value={monat} onChange={e => e.target.value && setMonat(e.target.value)} className={feld} aria-label="Monat" />
          {d && <span className="text-muted-foreground">Summe <b className="text-foreground tabular-nums">{dauer(d.summe_min)}</b> an {d.tage} {d.tage === 1 ? 'Tag' : 'Tagen'}</span>}
          <div className="ml-auto flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => herunterladen('csv')} data-testid="zeit-export-csv">CSV</Button>
            <Button size="sm" variant="outline" onClick={() => herunterladen('pdf')} data-testid="zeit-export-pdf">PDF</Button>
            <Button size="sm" variant="outline" onClick={() => setForm({ tag: heuteIso(), von: '09:00', bis: '17:00', pause_min: 0, notiz: '', grund: '' })}>Zeit nachtragen</Button>
          </div>
        </div>
        {d?.warnungen?.length > 0 && (
          <div className="space-y-1" data-testid="zeit-warnungen">
            {d.warnungen.map(w => <p key={w} className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400"><AlertTriangle className="w-3.5 h-3.5 flex-none mt-px" />{w}</p>)}
          </div>
        )}
        {d?.offene_freigaben?.length > 0 && (
          <div className="rounded-xl border border-border p-3 space-y-2" data-testid="zeit-freigaben">
            <h3 className="text-xs font-bold">Korrekturen zur Freigabe ({d.offene_freigaben.length})</h3>
            {d.offene_freigaben.map(f => (
              <div key={f.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                <span className="font-semibold">{f.name}</span>
                <span className="tabular-nums">{tagText(f.tag)} · {uhr(f.start)} – {uhr(f.ende)} · {dauer(f.minuten)}</span>
                <span className="text-muted-foreground flex-1 min-w-[120px]">{f.grund}</span>
                <Button size="sm" variant="outline" onClick={() => freigeben(f.id)}>Freigeben</Button>
              </div>
            ))}
          </div>
        )}
        {d?.team && (
          <div className="flex flex-wrap gap-2">
            {d.team.map(t => (
              <button key={t.key} onClick={() => setWer(t.key)}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${d.person.key === t.key ? 'border-primary bg-primary/10 text-primary font-semibold' : 'border-border hover:bg-accent'}`}>
                {t.name} · <span className="tabular-nums">{dauer(t.summe_min)}</span>{t.laeuft ? ' · läuft' : ''}
              </button>
            ))}
          </div>
        )}
        {d?.team && vertrag && (
          <div className="flex flex-wrap items-end gap-3 text-xs" data-testid="zeit-vertrag">
            <label>Vertrag<br /><select value={vertrag.art} onChange={e => setVertrag({ ...vertrag, art: e.target.value })} className={feld}>
              <option value="frei">frei</option><option value="minijob">Minijob</option><option value="teilzeit">Teilzeit</option><option value="vollzeit">Vollzeit</option></select></label>
            <label>Std. pro Woche<br /><input type="number" min="0" max="80" step="0.5" value={vertrag.stunden_woche ?? ''} onChange={e => setVertrag({ ...vertrag, stunden_woche: e.target.value === '' ? '' : Number(e.target.value) })} className={`${feld} w-24`} /></label>
            <label>Stundenlohn (€)<br /><input type="number" min="0" step="0.01" value={vertrag.stundenlohn ?? ''} onChange={e => setVertrag({ ...vertrag, stundenlohn: e.target.value === '' ? '' : Number(e.target.value) })} className={`${feld} w-24`} /></label>
            <Button size="sm" variant="outline" onClick={vertragSpeichern}>Speichern</Button>
          </div>
        )}
        {form && (
          <div className="flex flex-wrap items-end gap-3 rounded-xl bg-muted/60 p-3">
            <label className="text-xs">Tag<br /><input id="zeit-f-tag" type="date" max={heuteIso()} value={form.tag} onChange={e => setForm({ ...form, tag: e.target.value })} className={feld} /></label>
            <label className="text-xs">Von<br /><input id="zeit-f-von" type="time" value={form.von} onChange={e => setForm({ ...form, von: e.target.value })} className={feld} /></label>
            <label className="text-xs">Bis<br /><input id="zeit-f-bis" type="time" value={form.bis} onChange={e => setForm({ ...form, bis: e.target.value })} className={feld} /></label>
            <label className="text-xs">Pause (Min)<br /><input id="zeit-f-pause" type="number" min="0" max="600" value={form.pause_min} onChange={e => setForm({ ...form, pause_min: e.target.value })} className={`${feld} w-20`} /></label>
            <label className="text-xs flex-1 min-w-[140px]">Notiz<br /><input id="zeit-f-notiz" value={form.notiz} maxLength={300} onChange={e => setForm({ ...form, notiz: e.target.value })} className={`${feld} w-full`} /></label>
            {form.tag < heuteIso() && (
              <label className="text-xs w-full">Grund für den früheren Tag (mindestens 5 Zeichen)<br />
                <input id="zeit-f-grund" value={form.grund || ''} maxLength={300} onChange={e => setForm({ ...form, grund: e.target.value })} className={`${feld} w-full`} placeholder="z. B. Ausstempeln vergessen" /></label>
            )}
            <Button size="sm" onClick={speichern}>{form.id ? 'Ändern' : 'Eintragen'}</Button>
            <Button size="sm" variant="outline" onClick={() => setForm(null)}>Abbrechen</Button>
          </div>
        )}
        {d?.tage_plan?.some(t => t.geplant_min > 0) && (
          <div className="overflow-x-auto" data-testid="zeit-plan">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground"><tr>
                <th className="py-1.5 text-left font-semibold">Tag</th><th className="py-1.5 text-right font-semibold">Geplant</th>
                <th className="py-1.5 text-right font-semibold">Erfasst</th><th className="py-1.5 pl-3 text-left font-semibold">Einsätze</th>
              </tr></thead>
              <tbody>
                {d.tage_plan.filter(t => t.geplant_min > 0 || t.ist_min > 0).map(t => (
                  <tr key={t.tag} className="border-t border-border/50 align-top">
                    <td className="py-1.5 whitespace-nowrap">{tagText(t.tag)}</td>
                    <td className="py-1.5 text-right tabular-nums">{t.geplant_min ? stdText(t.geplant_min) : '–'}</td>
                    <td className={`py-1.5 text-right tabular-nums font-semibold ${t.geplant_min && !t.ist_min && t.tag < heuteIso() ? 'text-amber-700 dark:text-amber-400' : ''}`}>{t.ist_min ? stdText(t.ist_min) : '–'}</td>
                    <td className="py-1.5 pl-3 text-xs text-muted-foreground">{t.geplant.map(g => `${g.start || ''} ${g.titel}`.trim()).join(' · ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground"><tr>
              <th className="py-1.5 text-left font-semibold">Tag</th><th className="py-1.5 text-left font-semibold">Zeit</th>
              <th className="py-1.5 text-right font-semibold">Pause</th><th className="py-1.5 text-right font-semibold">Dauer</th>
              <th className="py-1.5 pl-3 text-left font-semibold">Notiz</th><th />
            </tr></thead>
            <tbody>
              {(d?.eintraege || []).map(e => (
                <tr key={e.id} className="border-t border-border/50 align-top">
                  <td className="py-1.5 whitespace-nowrap">{tagText(e.tag)}</td>
                  <td className="py-1.5 whitespace-nowrap tabular-nums">{uhr(e.start)} – {e.laeuft ? 'läuft' : uhr(e.ende)}</td>
                  <td className="py-1.5 text-right tabular-nums">{e.pause_min ? `${e.pause_min} Min` : '–'}</td>
                  <td className="py-1.5 text-right tabular-nums font-semibold">{dauer(e.minuten)}</td>
                  <td className="py-1.5 pl-3 text-xs text-muted-foreground">
                    {e.notiz}{e.quelle === 'nachtrag' && <span className="ml-1 rounded-full border border-border px-1.5">nachgetragen</span>}
                    {e.geaendert && <span className="ml-1 rounded-full border border-border px-1.5">geändert</span>}
                    {e.freigabe === 'offen' && <span className="ml-1 rounded-full border border-amber-500/60 px-1.5 text-amber-700 dark:text-amber-400">wartet auf Freigabe</span>}
                    {e.hinweis && <div className="text-amber-700 dark:text-amber-400">{e.hinweis}</div>}
                  </td>
                  <td className="py-1.5 text-right whitespace-nowrap">
                    {!e.laeuft && <button aria-label="Eintrag ändern" className="p-1 text-muted-foreground hover:text-foreground"
                      onClick={() => setForm({ id: e.id, tag: e.tag, von: hhmm(e.start), bis: hhmm(e.ende), pause_min: e.pause_min, notiz: e.notiz, grund: '' })}><Pencil className="w-3.5 h-3.5" /></button>}
                    {d.ist_inhaber && <button aria-label="Eintrag löschen" className="p-1 text-muted-foreground hover:text-destructive" onClick={() => loeschen(e)}><Trash2 className="w-3.5 h-3.5" /></button>}
                  </td>
                </tr>
              ))}
              {d && !d.eintraege.length && <tr><td colSpan={6} className="py-4 text-sm text-muted-foreground">In diesem Monat ist noch nichts erfasst.</td></tr>}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
